import { keepPreviousData, useQuery } from "@tanstack/react-query";

import type {
  Entitlement,
  Environment,
  NetworkMembership,
  PlayerUsage,
  Project,
  RateCard,
  Viewer,
} from "@magicweave/api";

import { useApi } from "./context";

/**
 * The reads every screen's frame makes: who you are, your games and their
 * environments, the draft, the plan, the rate card and the network membership.
 *
 * Apart from `hooks.ts` so the first paint carries these and not the hundred
 * reads the screens make, which load with their screens. `hooks.ts` re-exports
 * all of them, so a screen still reads every hook from one place.
 */

export const keys = {
  viewer: ["viewer"] as const,
  projects: (orgId: string) => ["org", orgId, "projects"] as const,
  project: (projectId: string) => ["project", projectId] as const,
  environments: (projectId: string) => ["project", projectId, "environments"] as const,
  releases: (projectId: string) => ["project", projectId, "releases"] as const,
  setupStatus: (projectId: string) => ["project", projectId, "setup-status"] as const,
  environment: (envId: string) => ["environment", envId] as const,
  credentials: (envId: string) => ["environment", envId, "credentials"] as const,
  installs: (envId: string) => ["environment", envId, "installs"] as const,
  members: (orgId: string) => ["org", orgId, "members"] as const,
  entitlement: (orgId: string) => ["org", orgId, "entitlement"] as const,
  membership: (orgId: string) => ["org", orgId, "network-membership"] as const,
  networkShelf: (orgId: string) => ["org", orgId, "network-shelf"] as const,
  networkAcceptance: (orgId: string) => ["org", orgId, "network-acceptance"] as const,
  networkAcceptanceSetting: (orgId: string) =>
    ["org", orgId, "network-acceptance-setting"] as const,
  coinStanding: (orgId: string) => ["org", orgId, "coin-standing"] as const,
  planUsage: (orgId: string) => ["org", orgId, "usage"] as const,
  playerUsage: (orgId: string) => ["org", orgId, "players", "usage"] as const,
  usageGuardrails: (orgId: string) => ["org", orgId, "usage", "guardrails"] as const,
  planUsageHistory: (orgId: string) => ["org", orgId, "usage", "history"] as const,
  plans: ["plans"] as const,
  billing: (orgId: string) => ["org", orgId, "billing"] as const,
  invoice: (orgId: string, invoiceId: string) => ["org", orgId, "invoice", invoiceId] as const,
  taxProfile: (orgId: string) => ["org", orgId, "tax-profile"] as const,
  creditPacks: (orgId: string) => ["org", orgId, "credit-packs"] as const,
  rateCard: ["rate-card"] as const,
  invitations: (orgId: string) => ["org", orgId, "invitations"] as const,
  roles: ["roles"] as const,
  players: (envId: string, cursor: string, q: string) =>
    ["environment", envId, "players", cursor, q] as const,
  player: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId] as const,
  currencies: (envId: string) => ["environment", envId, "currencies"] as const,
  stats: (envId: string) => ["environment", envId, "stats"] as const,
  leaderboards: (envId: string) => ["environment", envId, "leaderboards"] as const,
  standings: (envId: string, leaderboard: string, period: string, around: string) =>
    ["environment", envId, "leaderboards", leaderboard, period, around] as const,
  playerStandings: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId, "standings"] as const,
  playerStreaks: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId, "streaks"] as const,
  activations: (envId: string) => ["environment", envId, "activations"] as const,
  trace: (envId: string, traceId: string) => ["environment", envId, "trace", traceId] as const,
  signInProviders: (envId: string) => ["environment", envId, "sign-in-providers"] as const,
  integrations: (envId: string) => ["environment", envId, "integrations"] as const,
  integrationAudit: (envId: string) => ["environment", envId, "integrations", "audit"] as const,
  adNetworks: (envId: string) => ["environment", envId, "ad-networks"] as const,
  adRevenue: (envId: string) => ["environment", envId, "ad-revenue"] as const,
  redemptions: (envId: string, state: string) =>
    ["environment", envId, "redemptions", state] as const,
  // ---- packet G: player documents + saves ---------------------------------
  playerDocumentCollections: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId, "documents"] as const,
  playerDocuments: (envId: string, playerId: string, collection: string, after: string) =>
    ["environment", envId, "player", playerId, "documents", collection, after] as const,
  playerSaves: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId, "saves"] as const,
  playerSave: (envId: string, playerId: string, slot: string) =>
    ["environment", envId, "player", playerId, "saves", slot] as const,
  // ---- packet H: rooms ------------------------------------------------------
  rooms: (envId: string) => ["environment", envId, "rooms"] as const,
  roomState: (envId: string, room: string) =>
    ["environment", envId, "rooms", room, "state"] as const,
};

/**
 * openapi-fetch returns `{ data, error }` and our middleware throws on failure,
 * so `error` is always undefined by the time we see the result. This unwraps
 * `data` and turns the impossible case into a real error rather than letting
 * `undefined` propagate into a component as a blank screen.
 */
export function unwrap<T>(result: { data?: T }): T {
  if (result.data === undefined) {
    throw new Error("the API returned no body for a successful response");
  }
  return result.data;
}

export function useViewer() {
  const api = useApi();
  return useQuery({
    queryKey: keys.viewer,
    queryFn: async (): Promise<Viewer> => unwrap(await api.GET("/v1/me")),
    // Who you are and which orgs you belong to changes on the order of never.
    staleTime: 5 * 60_000,
  });
}

export function useProjects(orgId: string | undefined, options: { pollMs?: number } = {}) {
  const api = useApi();
  const { pollMs } = options;
  return useQuery({
    queryKey: keys.projects(orgId ?? ""),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<Project[]> => {
      const res = await api.GET("/v1/orgs/{orgId}/projects", {
        params: { path: { orgId: orgId as string } },
      });
      return unwrap(res).projects;
    },
    // Only while a screen is waiting for a game to appear — Claude is making
    // one somewhere else — and only while the tab is in front.
    refetchInterval: pollMs
      ? () => (document.visibilityState === "visible" ? pollMs : false)
      : false,
  });
}

export function useProject(projectId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.project(projectId),
    queryFn: async (): Promise<Project> =>
      unwrap(await api.GET("/v1/projects/{projectId}", { params: { path: { projectId } } })),
    enabled: Boolean(projectId),
  });
}

/** One project's environments. Exported so the games page reads them without a hook per game. */
export async function readEnvironments(
  api: ReturnType<typeof useApi>,
  projectId: string,
): Promise<Environment[]> {
  const res = await api.GET("/v1/projects/{projectId}/environments", {
    params: { path: { projectId } },
  });
  return unwrap(res).environments;
}

export function useEnvironments(projectId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.environments(projectId),
    queryFn: () => readEnvironments(api, projectId),
    // The shell asks for these on every screen, including the ones outside a
    // project. Without this the query runs with an empty id and the server
    // answers 400 — harmless to the page, and a real error in the console for
    // anyone reading it while debugging something else.
    enabled: Boolean(projectId),
  });
}

export function useDraftDocuments(projectId: string, { enabled = true } = {}) {
  const api = useApi();
  return useQuery({
    enabled,
    queryKey: [...keys.project(projectId), "draft"],
    queryFn: async () =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/draft", { params: { path: { projectId } } }),
      ).documents,
  });
}

/**
 * What a studio's plan includes, and what has been used of it.
 *
 * Organisation-scoped, like the team, and for the same reason: a plan covers a
 * whole studio, and reading it behind a project switcher would suggest it could
 * differ from one project to the next. It cannot.
 *
 * Read the capabilities from here and never from `usePlans()`. A studio may be
 * pinned to an older version of the same plan than the catalogue offers — the
 * server keeps them apart deliberately, and v1's own billing screen conflates
 * them and shows a cap that is not the one being enforced.
 *
 * `keepPrevious` is for the top strip and for nothing else. The chip lives in
 * the frame rather than in a screen, so an organisation switch re-keys a query
 * that is on screen the whole time, and the default behaviour empties it for as
 * long as the read takes — a chip that vanishes and comes back on the two
 * screens somebody opened *because* they were thinking about credits. Holding
 * the previous answer keeps it in place, and the caller dims what it is holding
 * until `isPlaceholderData` clears. A screen must not pass this: the plan
 * screen showing one organisation's allowance under another's heading is worth
 * more than the flicker it would save.
 */
export function useEntitlement(orgId: string, options?: { keepPrevious?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: keys.entitlement(orgId),
    queryFn: async (): Promise<Entitlement> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/entitlement", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
    ...(options?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/**
 * Whether an organisation is in the MagicCoin network, and how far along.
 *
 * One query, one key, shared by every screen that has to gate on membership —
 * the sidebar, the Economy panel's synthesised MagicCoin row, and the network
 * shelf all read this hook rather than each fetching `/network/membership`
 * themselves, on the same argument `useEntitlement` above makes: two readers
 * of the same fact on two keys can disagree about which organisation is a
 * member for as long as their caches are out of step.
 *
 * `keepPrevious` follows `useAllowanceChip`'s rule and not `useEntitlement`'s
 * default: opt in only for chrome that must not flicker or vanish across an
 * org switch (a sidebar badge), and call this plain wherever the answer is
 * treated as ground truth (a gate that decides what renders, a card that
 * states current standing) — those must not hold a previous organisation's
 * membership on screen while a new one is still loading.
 */
export function useMembership(orgId: string, options?: { keepPrevious?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: keys.membership(orgId),
    queryFn: async (): Promise<NetworkMembership> =>
      unwrap(
        await api.GET("/v1/orgs/{orgId}/network/membership", { params: { path: { orgId } } }),
      ),
    enabled: Boolean(orgId),
    ...(options?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/**
 * Every player the organisation has ever signed up, against its plan's
 * lifetime limit, split by project and environment.
 *
 * A read of identity's counter rather than anything summed here: the server's
 * `onboarded` is the number the sign-up gate compares, so the screen and the
 * gate cannot disagree about whether there is room.
 */
export function usePlayerUsage(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.playerUsage(orgId),
    queryFn: async (): Promise<PlayerUsage> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/players/usage", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
  });
}

/**
 * What one metered action costs, per meter, and which price list said so.
 *
 * Platform-wide rather than per-organisation — one active card for everybody,
 * `decisions/0025` decision 4 — so it is one query on one key however many
 * charging controls a screen puts on itself.
 *
 * Cached like the catalogue but not as hard. A card is not grandfathered and has
 * no undo, so a price staff changed is in force for everybody at once and this
 * is the read that has to notice: a console holding a stale figure quotes a
 * price nobody will be charged, which is the failure `SHIPPED_RATES` was
 * deleted for.
 */
export function useRateCard() {
  const api = useApi();
  return useQuery({
    queryKey: keys.rateCard,
    queryFn: async (): Promise<RateCard> => unwrap(await api.GET("/v1/rate-card")),
    staleTime: 60_000,
  });
}
